
<template>
  <div class="location-selector">
    <!-- 下拉选择器 -->
    <el-select
        v-model="selectedLocation"
        placeholder="选择水库或灌区"
        clearable
        filterable
        @change="handleLocationChange"
        class="location-select"
    >
      <el-option-group
          v-for="group in locationGroups"
          :key="group.label"
          :label="group.label"
      >
        <el-option
            v-for="item in group.options"
            :key="item.id"
            :label="item.name"
            :value="item.id"
        >
          <span style="float: left">{{ item.name }}</span>
          <span style="float: right; color: #8492a6; font-size: 13px">
            {{ item.type || '地点' }}
          </span>
        </el-option>
      </el-option-group>
    </el-select>
  </div>
</template>

<script>
import * as Cesium from 'cesium'
import {district, hyStations, industrialPark, irrigation, pool, protection, pumping} from '@/common/js/position'

export default {
  name: 'ReservoirControl',
  data() {
    return {
      selectedLocation: null,
      locationGroups: []
    }
  },
  mounted() {
    this.initLocationData()
  },
  methods: {
    initLocationData() {
      // 组织地点数据
      this.locationGroups = [
        {
          label: '水库',
          options: hyStations.map(item => ({
            ...item,
            type: '水库'
          }))
        },
        {
          label: '灌区',
          options: irrigation.map(item => ({
            ...item,
            type: '灌区'
          }))
        },
        {
          label: '县区',
          options: district.map(item => ({
            ...item,
            type: '县区'
          }))
        },
        {
          label: '工业区',
          options: industrialPark.map(item => ({
            ...item,
            type: '工业区'
          }))
        },
        {
          label: '泵站',
          options: pumping.map(item => ({
            ...item,
            type: '泵站'
          }))
        },
        {
          label: '调蓄池',
          options: pool.map(item => ({
            ...item,
            type: '调蓄池'
          }))
        },
        {
          label: '保护区',
          options: protection.map(item => ({
            ...item,
            type: '保护区'
          }))
        }
      ]
    },

        handleLocationChange(locationId) {
      if (!locationId) {
        this.selectedLocation = null
        return
      }

      // 根据ID查找对应的位置信息
      const location = this.findLocationById(locationId)
      if (location) {
        this.flyToLocation(location)
      }
    },

    findLocationById(id) {
      for (const group of this.locationGroups) {
        const location = group.options.find(item => item.id === id)
        if (location) {
          return location
        }
      }
      return null
    },

    findLocationByName(name) {
      for (const group of this.locationGroups) {
        const location = group.options.find(item => item.name === name)
        if (location) {
          return location
        }
      }
      return null
    },

    flyToLocation(location) {
      if (!window.viewer) {
        console.error('Cesium viewer 未初始化')
        return
      }

      const longitude = location.location[0]
      const latitude = location.location[1]
      const groundHeight = location.location[2] || 450

      // 根据类型确定相机参数：高度、俯角、航向
      const category = location.categories || ''
      let camAlt, camHeading, camPitch

      if (category === 'reservoir' || location.type === 'dam') {
        // 水库：高度 3500m，俯角 -55°，朝北偏东28°
        camAlt = Math.max(groundHeight + 3000, 3500)
        camHeading = 0.5 // ~28.6°
        camPitch = -0.9599 // -55°
      } else if (category === 'regulating') {
        // 调蓄水库：高度 3000m，俯角 -55°
        camAlt = Math.max(groundHeight + 2500, 3000)
        camHeading = 0.5
        camPitch = -0.9599
      } else if (category === 'pump') {
        // 泵站：高度 2000m，俯角 -50°
        camAlt = Math.max(groundHeight + 1500, 2000)
        camHeading = 0.5
        camPitch = -0.8727 // -50°
      } else if (category === 'pool') {
        // 调蓄池：高度 2000m，俯角 -50°
        camAlt = Math.max(groundHeight + 1500, 2000)
        camHeading = 0.5
        camPitch = -0.8727
      } else if (category === 'irrigation') {
        // 灌区：高度 6000m，俯角 -55°
        camAlt = 6000
        camHeading = 0.0
        camPitch = -0.9599
      } else if (category === 'industrial') {
        // 工业园区：高度 4000m，俯角 -55°
        camAlt = 4000
        camHeading = 0.5
        camPitch = -0.9599
      } else {
        // 县区/保护区/其他：高度 10000m，俯角 -60°
        camAlt = 10000
        camHeading = 0.0
        camPitch = -1.0472 // -60°
      }

      // 计算倾斜视角的相机偏移位置
      // 相机需置于目标点反方向，使视线中心对准目标
      // 水平偏移 = 高度 / tan(|pitch|)
      // heading=0(朝北) → 相机在目标正南方
      // heading=0.5(朝NNE) → 相机在目标的SSW方向
      const absPitch = Math.abs(camPitch)
      const horizontalOffset = camAlt / Math.tan(absPitch) // 米
      const latRad = latitude * Math.PI / 180
      const metersPerDegLat = 111320
      const metersPerDegLng = 111320 * Math.cos(latRad)
      // 相机在目标反方向：减去偏移分量
      const camLat = latitude - (horizontalOffset * Math.cos(camHeading)) / metersPerDegLat
      const camLng = longitude - (horizontalOffset * Math.sin(camHeading)) / metersPerDegLng

      // 飞行到指定位置
      window.viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
            camLng,
            camLat,
            camAlt
        ),
        orientation: {
          heading: camHeading,
          pitch: camPitch,
          roll: 0.0
        },
        duration: 2.0,
        complete: () => {
          console.log(`已定位到: ${location.name} 目标(${longitude.toFixed(4)}°E, ${latitude.toFixed(4)}°N) 相机(${camLng.toFixed(4)}°E, ${camLat.toFixed(4)}°N) alt=${camAlt}m pitch=${(camPitch * 180 / Math.PI).toFixed(1)}° offset=${horizontalOffset.toFixed(0)}m`)
        }
      })

      // 添加定位成功提示
      this.$message.success(`已定位到: ${location.name}`)
    },

    // 外部调用的方法：通过名称定位
    flyToByName(name) {
      const location = this.findLocationByName(name)
      if (location) {
        this.selectedLocation = location.id
        this.flyToLocation(location)
        return true
      }
      this.$message.error(`未找到地点: ${name}`)
      return false
    },

    // 外部调用的方法：通过ID定位
    flyToById(id) {
      const location = this.findLocationById(id)
      if (location) {
        this.selectedLocation = id
        this.flyToLocation(location)
        return true
      }
      this.$message.error(`未找到ID为 ${id} 的地点`)
      return false
    }
  }
}
</script>

<style scoped>
.location-selector {
  position: absolute;
  top: 92px;
  left: 22px;
  z-index: 9999;
  padding: 8px;
  border: 1px solid rgba(0, 242, 248, 0.18);
  border-radius: 8px;
  background: rgba(2, 23, 30, 0.72);
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(10px);
}

.location-select {
  width: 220px;
}

.location-select ::v-deep(.el-input__inner) {
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(0, 242, 248, 0.28);
  color: #e6fbff;
  font-weight: 500;
  border-radius: 6px;
  height: 34px;
  line-height: 34px;
  box-shadow: none;
  transition: all 0.2s ease;
}

.location-select ::v-deep(.el-input__inner:hover) {
  background: rgba(0, 242, 248, 0.08);
  border-color: rgba(0, 242, 248, 0.5);
}

.location-select ::v-deep(.el-input__inner:focus) {
  background: rgba(0, 242, 248, 0.08);
  border-color: rgba(0, 242, 248, 0.62);
  box-shadow: 0 0 16px rgba(0, 242, 248, 0.14);
  outline: none;
}

/* 下拉框样式 */
.location-select ::v-deep(.el-select-dropdown) {
  background-color: #fff;
  border: 1px solid #409eff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.location-select ::v-deep(.el-select-dropdown__item) {
  color: #333;
}

.location-select ::v-deep(.el-select-dropdown__item:hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

/* 分组选项标题 */
.location-select ::v-deep(.el-select-group__title) {
  color: #409eff;
  font-weight: bold;
  background-color: rgba(64, 158, 255, 0.1);
}

/deep/ .el-message {
  top: 100px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
}

</style>
